/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {Component, Input, OnInit, inject} from '@angular/core';

import {GeneralDataHandlingService} from '../../../../../services/general.data-handling.service';
import {FormFieldAttribute} from '../../../../../models/FormCategory';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {IxModule} from '@siemens/ix-angular';
import {KeyValueComponent} from '../key-value/key-value.component';
import {PassportDto} from '../../../../../models/Passport';
import {PassportTranslationService} from '../../../../../services/passport-translation.service';
import {
  getDocumentUrlPath,
  isDocumentObjectAttribute,
  toDocumentObject,
  toSafeHttpUrl,
} from '../../../utils/document-object';

@Component({
  selector: 'app-document',
  imports: [IxModule, TranslateModule, KeyValueComponent],
  templateUrl: './document.component.html',
  styleUrl: './document.component.scss',
})
export class DocumentComponent implements OnInit {
  private readonly dataHandler = inject(GeneralDataHandlingService);
  private readonly passportTranslation = inject(PassportTranslationService);
  private readonly translate = inject(TranslateService);

  @Input({required: true}) attribute!:
    | FormFieldAttribute
    | FormFieldAttribute[];
  @Input({required: true}) passport!: PassportDto['data'];
  @Input({required: true}) schemaId!: string;

  label!: string;
  icon!: string;

  //image
  imageSrc!: string;

  // document
  url!: string;
  urlLabel!: string;
  language?: string;
  languageLabel?: string;

  async ngOnInit() {
    if (
      !Array.isArray(this.attribute) &&
      isDocumentObjectAttribute(this.attribute)
    ) {
      await this.objectDocument(this.attribute);
    } else if (!Array.isArray(this.attribute)) {
      const isImage = await this.checkImage(
        this.dataHandler.getValueByPath(
          this.passport,
          this.attribute.valuePath,
        ),
      );
      if (isImage) {
        this.imageResource(this.attribute);
      } else {
        this.basicDocument(this.attribute);
      }
    } else if (DocumentComponent.isDocumentObjectList(this.attribute)) {
      const urlPath = getDocumentUrlPath({
        attributes: this.attribute,
      } as FormFieldAttribute);
      await this.applyDocumentObject(
        toDocumentObject(this.passport, urlPath),
        urlPath,
      );
    } else {
      this.advancedDocument(this.attribute);
    }
  }

  checkImage(url: string): Promise<boolean> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  }

  private imageResource(attribute: FormFieldAttribute) {
    this.label = this.passportTranslation.translateByPath(
      attribute.valuePath,
      this.schemaId,
      attribute.translation.label,
    );
    this.icon = 'image';
    this.imageSrc =
      this.dataHandler.getValueByPath(this.passport, attribute.valuePath) ?? '';
  }

  private basicDocument(attribute: FormFieldAttribute) {
    this.label = this.passportTranslation.translateByPath(
      attribute.valuePath,
      this.schemaId,
      attribute.translation.label,
    );
    this.icon = 'document-link';
    this.url = toSafeHttpUrl(
      this.dataHandler.getValueByPath(this.passport, attribute.valuePath),
    );
    this.urlLabel =
      this.dataHandler.getValueByPath(this.passport, attribute.valuePath) ?? '';
  }

  /**
   * Single-object document (`document` with sub-attributes, e.g.
   * `{url, language, resourceTitle}`): links the URL, labels it with the
   * resource title (fallback: the URL) and shows the language if present.
   * A legacy plain-string value is treated as the URL.
   */
  private async objectDocument(attribute: FormFieldAttribute) {
    this.label = this.passportTranslation.translateByPath(
      attribute.valuePath,
      this.schemaId,
      attribute.translation.label,
    );
    const urlPath = getDocumentUrlPath(attribute);
    const document = toDocumentObject(
      this.dataHandler.getValueByPath(this.passport, attribute.valuePath),
      urlPath,
    );
    await this.applyDocumentObject(document, urlPath);
  }

  /** Whether the `list:document` item sub-attributes use the `{url, resourceTitle, language}` shape. */
  private static isDocumentObjectList(attributes: FormFieldAttribute[]) {
    return (
      attributes.some((attr) => attr.valuePath === 'resourceTitle') &&
      attributes.some((attr) => attr.type === 'url' || attr.type === 'document')
    );
  }

  /** Sets the link (or image), its title and the language from a document object. */
  private async applyDocumentObject(
    document: Record<string, unknown> | undefined,
    urlPath: string,
  ) {
    const rawUrl = this.dataHandler.getValueByPath(document, urlPath);
    const url = typeof rawUrl === 'string' ? rawUrl : '';
    const rawTitle = this.dataHandler.getValueByPath(document, 'resourceTitle');
    const rawLanguage = this.dataHandler.getValueByPath(document, 'language');
    const title = typeof rawTitle === 'string' ? rawTitle : '';

    this.urlLabel = title || url;
    this.language =
      typeof rawLanguage === 'string' && rawLanguage ? rawLanguage : undefined;
    this.languageLabel = this.language
      ? this.localizeLanguage(this.language)
      : undefined;

    if (url && (await this.checkImage(url))) {
      this.icon = 'image';
      this.imageSrc = url;
      return;
    }

    this.icon = 'document-link';
    this.url = toSafeHttpUrl(url);
  }

  /** Language name in the UI language; the raw code when it is not a valid language tag. */
  private localizeLanguage(code: string): string {
    try {
      const locale =
        this.translate.currentLang || this.translate.defaultLang || 'en';
      return (
        new Intl.DisplayNames([locale], {type: 'language'}).of(code) ?? code
      );
    } catch {
      return code;
    }
  }

  private advancedDocument(attributes: FormFieldAttribute[]) {
    // Set icon based on mimeType
    const mimeType = attributes.find(
      (attr) => attr.name === 'mimeType',
    )?.valuePath;
    if (!mimeType) {
      this.icon = 'document-link';
    } else {
      this.icon = DocumentComponent.getIcon(
        this.dataHandler.getValueByPath(this.passport, mimeType) || '',
      );
    }

    // Set URL
    const rawUrl =
      this.dataHandler.getValueByPath(
        this.passport,
        attributes!.find((e) => e.type === 'url')!.valuePath,
      ) || '';
    this.url = toSafeHttpUrl(rawUrl);

    // Set Label
    const labelValuePath = attributes!.find(
      (e) => e.type === 'select' && e.name !== 'mimeType',
    )?.valuePath;
    if (!labelValuePath) {
      this.urlLabel = rawUrl;
    } else {
      const labelValue = this.dataHandler.getValueByPath(
        this.passport,
        labelValuePath,
      );
      this.urlLabel = labelValue || rawUrl;
    }
  }

  static getIcon(mimeType: string): string {
    switch (mimeType.split('/').at(-1)) {
      case 'pdf':
        return 'pdf-document';
      default:
        return 'document-link';
    }
  }
}
